import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, PackageSearch, Printer } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { fmtDate, fmtGNF, PAYMENT_STATUS_STYLES, STATUS_STYLES } from "@/lib/format";
import { Loading } from "@/components/ProtectedRoute";
import { OrderTracker } from "@/components/OrderTracker";
import { ReturnRequest } from "@/components/ReturnRequest";
import { useAuth } from "@/context/AuthContext";

export function Orders() {
  const [orders, setOrders] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () =>
    api
      .get("/orders")
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Impossible de charger vos commandes"));

  useEffect(() => {
    load();
  }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/orders/${id}/cancel`);
      toast.success("Commande annulée");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!orders) return <Loading label="Chargement de vos commandes…" />;

  if (orders.length === 0)
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="orders-empty">
        <PackageSearch className="mx-auto h-12 w-12 text-slate-300" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Aucune commande</h1>
        <p className="mt-2 text-sm text-slate-500">Vos commandes apparaîtront ici après votre premier achat.</p>
        <Link to="/catalogue" className="ds-btn-primary mt-5">Voir le catalogue</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6" data-testid="orders-page">
      <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900 sm:text-3xl">
        <ClipboardList className="h-6 w-6 text-gold" /> Mes commandes
      </h1>

      <div className="mt-5 space-y-3">
        {orders.map((o) => (
          <div key={o.id} className="ds-card p-4" data-testid={`order-card-${o.number}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-num text-sm font-bold text-slate-900">{o.number}</p>
                <p className="text-xs text-slate-500">{fmtDate(o.created_at)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`ds-chip ${STATUS_STYLES[o.status] || ""}`} data-testid={`order-status-${o.number}`}>
                  {o.status}
                </span>
                <span className={`ds-chip ${PAYMENT_STATUS_STYLES[o.payment_status] || ""}`}>
                  Paiement : {o.payment_status}
                </span>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-num font-bold text-brand">{fmtGNF(o.total)}</span>
              <span className="text-slate-500">{o.items.length} article(s)</span>
              <span className="text-slate-500">{o.payment_method_label}</span>
              <span className="text-slate-500">{o.zone_name}</span>
            </div>

            <div className="mt-3 border-t border-slate-200 pt-3">
              <OrderTracker order={o} />
            </div>

            {o.delivery_mode === "pickup" && o.pickup_code && !o.pickup_code_used && (
              <div
                className="mt-3 flex items-center gap-4 rounded-xl border border-brand/30 bg-brand-light/60 p-3"
                data-testid={`pickup-pass-${o.number}`}
              >
                <div className="rounded-lg bg-white p-2">
                  <QRCodeCanvas value={o.pickup_code} size={84} level="M" includeMargin={false} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-brand">Bon de retrait à l'entrepôt</p>
                  <p className="font-num text-2xl font-bold tracking-widest text-slate-900">{o.pickup_code}</p>
                  <p className="mt-0.5 text-xs text-slate-600">
                    Présentez ce code {o.pickup_date ? `le ${o.pickup_date}` : ""} {o.pickup_slot || ""} — usage unique.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setOpen(open === o.id ? null : o.id)}
                className="ds-btn-ghost py-2 text-xs"
                data-testid={`order-toggle-${o.number}`}
              >
                {open === o.id ? "Masquer le détail" : "Voir le détail"}
              </button>
              {["Commande reçue", "Paiement en attente"].includes(o.status) && (
                <button
                  type="button"
                  onClick={() => cancel(o.id)}
                  className="ds-btn-ghost border-rose-200 py-2 text-xs text-rose-600"
                  data-testid={`order-cancel-${o.number}`}
                >
                  Annuler
                </button>
              )}
              <Link
                to={`/commande/${o.id}/bon-de-livraison`}
                className="ds-btn-ghost py-2 text-xs"
                data-testid={`order-receipt-${o.number}`}
              >
                <Printer className="h-3.5 w-3.5" /> Bon de livraison
              </Link>
              <ReturnRequest order={o} onDone={load} />
            </div>

            {open === o.id && (
              <div className="mt-4 space-y-3 border-t border-slate-200 pt-3" data-testid={`order-detail-${o.number}`}>
                {o.items.map((it) => (
                  <div key={it.id} className="flex items-center gap-3">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                      {it.image && <img src={mediaUrl(it.image)} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-semibold text-slate-900">{it.product_name}</p>
                      <p className="font-num text-xs text-slate-500">
                        {it.quantity} × {fmtGNF(it.unit_price)} {it.is_wholesale && "(gros)"}
                      </p>
                    </div>
                    <p className="font-num text-sm font-semibold">{fmtGNF(it.total)}</p>
                  </div>
                ))}
                <div className="space-y-1 border-t border-slate-200 pt-2 text-sm">
                  <Row label="Sous-total" value={fmtGNF(o.subtotal)} />
                  <Row label="Livraison" value={fmtGNF(o.delivery_fee)} />
                  <Row label="Total" value={fmtGNF(o.total)} bold />
                  <Row label="Adresse" value={o.address} />
                  <Row label="Téléphone" value={o.phone} />
                  <Row label="Statut livraison" value={o.delivery_status} />
                </div>
                <div>
                  <p className="ds-label">Journal détaillé</p>
                  <ol className="mt-2 space-y-2">
                    {o.history?.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand" />
                        <span>
                          <span className="font-semibold text-slate-800">{h.status}</span>
                          <span className="text-slate-500"> — {fmtDate(h.at)}</span>
                          {h.note && <span className="block text-slate-500">{h.note}</span>}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-slate-600">{label}</span>
    <span className={`text-right ${bold ? "font-num font-bold text-slate-900" : "text-slate-800"}`}>{value}</span>
  </div>
);

export function Account() {
  const { user, updateProfile, logout } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", address: user?.address || "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await updateProfile(form);
    setBusy(false);
    if (res.error) toast.error(res.error);
    else toast.success("Profil mis à jour");
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-8 sm:px-6" data-testid="account-page">
      <h1 className="ds-h1">Mon compte</h1>
      <p className="mt-1 text-sm text-slate-500">{user?.email} · rôle : {user?.role}</p>

      <form onSubmit={submit} className="ds-card mt-5 space-y-3 p-4">
        <div>
          <label className="ds-label">Nom complet</label>
          <input className="ds-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="account-name-input" />
        </div>
        <div>
          <label className="ds-label">Téléphone</label>
          <input className="ds-input mt-1" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="account-phone-input" />
        </div>
        <div>
          <label className="ds-label">Adresse de livraison</label>
          <textarea rows={2} className="ds-input mt-1" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="account-address-input" />
        </div>
        <button type="submit" disabled={busy} className="ds-btn-primary w-full" data-testid="account-save-button">
          Enregistrer
        </button>
      </form>

      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/commandes" className="ds-btn-ghost">Mes commandes</Link>
        {user?.role === "admin" && <Link to="/admin" className="ds-btn-ghost">Espace administrateur</Link>}
        {user?.role === "warehouse" && <Link to="/admin/stock" className="ds-btn-ghost">Espace entrepôt</Link>}
        <button type="button" onClick={logout} className="ds-btn-ghost border-rose-200 text-rose-600" data-testid="account-logout-button">
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
